import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApi from '../api';
import { CartesianGrid, XAxis, YAxis, Area, AreaChart, BarChart, Bar, ComposedChart, Line, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { ArrowDownIcon, ArrowUpIcon, MessageCircle, MessagesSquare, Users, Eye, Database } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { type ChartConfig } from "@/components/ui/chart";
import { TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoadingState } from '@/components/ui/LoadingState';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { calculateCostUsd } from "./modelPricing";

interface AdminAnalyticsData {
    total_conversations: number;
    total_messages: number;
    total_leads: number;
    total_tokens: number;
    llm_conversation_count: number;
    conversations_per_day: {
        day: string;
        conversation_count: number;
    }[];
    messages_per_day: {
        day: string;
        message_count: number;
    }[];
    leads_per_day: {
        day: string;
        lead_count: number;
    }[];
    visitors_per_day: {
        day: string;
        visitor_count: number;
    }[];
    conversations_by_organization: {
        organization_name: string;
        conversation_count: number;
    }[];
    messages_by_organization: {
        organization_name: string;
        message_count: number;
    }[];
    leads_by_organization: {
        organization_name: string;
        lead_count: number;
    }[];
    tokens_by_organization_model: {
        organization_name: string;
        model: string;
        total_tokens: number;
        input_tokens: number;
        output_tokens: number;
    }[];
    previous_period: {
        total_conversations: number;
        total_messages: number;
        total_leads: number;
        total_tokens: number;
    };
    time_range: string;
}

interface OrganizationOption {
    id: string;
    name: string;
}

interface UsageSummaryData {
    summary: {
        request_count: number;
        failure_count: number;
        failure_rate: number;
        request_units: number;
        unique_credentials: number;
        avg_duration_ms: number;
        max_duration_ms: number;
    };
    by_surface: {
        surface: string;
        request_count: number;
        failure_count: number;
    }[];
    trend: {
        period: string;
        surface: string;
        request_count: number;
        failure_count: number;
    }[];
    top_operations: {
        operation: string;
        surface: string;
        request_count: number;
        failure_count: number;
    }[];
}

interface UsageEventItem {
    id: string;
    organization_id: string;
    organization_name: string | null;
    surface: string;
    operation: string;
    credential_type: string | null;
    credential_id: string | null;
    status: string;
    status_code: number | null;
    duration_ms: number | null;
    request_units: number;
    created_at: string | null;
}

interface UsageEventsData {
    items: UsageEventItem[];
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
}

// Define time range options
const timeRangeOptions = [
    { value: "24h", label: "Last 24 Hours" },
    { value: "7d", label: "Last Week" },
    { value: "30d", label: "Last Month" },
    { value: "90d", label: "Last Quarter" },
    { value: "365d", label: "Last Year" }
];

const chartConfig = {
    conversations: {
        label: "Conversations",
        color: "hsl(var(--chart-1))",
    },
    messages: {
        label: "Messages", 
        color: "hsl(var(--chart-2))",
    },
    leads: {
        label: "Contacts",
        color: "hsl(var(--chart-3))",
    },
    visitors: {
        label: "New Visitors",
        color: "hsl(var(--chart-4))",
    },
} satisfies ChartConfig;

const modelSeriesColors = [
    "#2563eb", // blue
    "#16a34a", // green
    "#dc2626", // red
    "#7c3aed", // violet
    "#ea580c", // orange
    "#0891b2", // cyan
    "#ca8a04", // amber
    "#db2777", // pink
];

const AdminAnalyticsTab: React.FC = () => {
    const { get } = useApi();
    const navigate = useNavigate();
    const [analyticsData, setAnalyticsData] = useState<AdminAnalyticsData | null>(null);
    const [timeRange, setTimeRange] = useState("7d");
    const [isLoading, setIsLoading] = useState(true);

    const [tokenChartView, setTokenChartView] = useState<"tokens" | "cost">("cost");
    const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
    const [usageSummary, setUsageSummary] = useState<UsageSummaryData | null>(null);
    const [usageEvents, setUsageEvents] = useState<UsageEventsData | null>(null);
    const [usageLoading, setUsageLoading] = useState(false);
    const [usageEventsLoading, setUsageEventsLoading] = useState(false);
    const [usageSurface, setUsageSurface] = useState("all");
    const [usageOrganizationId, setUsageOrganizationId] = useState("all");
    const [usageStatus, setUsageStatus] = useState("all");
    const [usageOperation, setUsageOperation] = useState("");
    const [usageEventsPage, setUsageEventsPage] = useState(1);

    useEffect(() => {
        fetchAnalyticsData();
    }, [timeRange]);

    useEffect(() => {
        fetchOrganizations();
    }, []);

    useEffect(() => {
        fetchUsageSummary();
    }, [timeRange, usageSurface, usageOrganizationId]);

    useEffect(() => {
        fetchUsageEvents();
    }, [usageSurface, usageOrganizationId, usageStatus, usageOperation, usageEventsPage]);

    useEffect(() => {
        setUsageEventsPage(1);
    }, [usageSurface, usageOrganizationId, usageStatus, usageOperation]);

    const fetchAnalyticsData = async () => {
        setIsLoading(true);
        try {
            const response = await get(`/admin/analytics?time_range=${timeRange}`);
            setAnalyticsData(response.data);
        } catch (error) {
            console.error('Error fetching admin analytics:', error);
            toast.error('Failed to load analytics data');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchOrganizations = async () => {
        try {
            const response = await get('/admin/organizations/');
            setOrganizations((response.data || []).map((org: any) => ({
                id: org.id,
                name: org.name || org.id,
            })));
        } catch (error) {
            console.error('Error fetching organizations:', error);
        }
    };

    const fetchUsageSummary = async () => {
        setUsageLoading(true);
        try {
            const params = new URLSearchParams({
                time_range: timeRange,
                surface: usageSurface,
            });
            if (usageOrganizationId !== "all") {
                params.set("organization_id", usageOrganizationId);
            }
            const response = await get(`/admin/usage/summary?${params.toString()}`);
            setUsageSummary(response.data);
        } catch (error) {
            console.error('Error fetching usage summary:', error);
            toast.error('Failed to load API/MCP usage summary');
        } finally {
            setUsageLoading(false);
        }
    };

    const fetchUsageEvents = async () => {
        setUsageEventsLoading(true);
        try {
            const params = new URLSearchParams({
                surface: usageSurface,
                page: String(usageEventsPage),
                per_page: "20",
            });
            if (usageOrganizationId !== "all") {
                params.set("organization_id", usageOrganizationId);
            }
            if (usageStatus !== "all") {
                params.set("status", usageStatus);
            }
            if (usageOperation.trim()) {
                params.set("operation", usageOperation.trim());
            }
            const response = await get(`/admin/usage/events?${params.toString()}`);
            setUsageEvents(response.data);
        } catch (error) {
            console.error('Error fetching usage events:', error);
            toast.error('Failed to load API/MCP usage logs');
        } finally {
            setUsageEventsLoading(false);
        }
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        if (timeRange === "24h") {
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        } else if (timeRange === "7d") {
            return date.toLocaleDateString('en-US', { weekday: 'short' });
        } else if (timeRange === "30d") {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } else {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
    };

    const formatUsagePeriod = (dateStr: string) => {
        const date = new Date(dateStr);
        if (Number.isNaN(date.getTime())) return dateStr;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const formatPercent = (value: number) => `${(value * 100).toFixed(1)}%`;

    const calculatePercentageChange = (current: number, previous: number) => {
        if (previous === 0) return current > 0 ? 100 : 0;
        return ((current - previous) / previous) * 100;
    };

    const getMetricCards = () => {
        if (!analyticsData) return [];

        const conversationChange = calculatePercentageChange(
            analyticsData.total_conversations,
            analyticsData.previous_period.total_conversations
        );

        const messageChange = calculatePercentageChange(
            analyticsData.total_messages,
            analyticsData.previous_period.total_messages
        );

        const leadChange = calculatePercentageChange(
            analyticsData.total_leads,
            analyticsData.previous_period.total_leads
        );

        const tokenChange = calculatePercentageChange(
            analyticsData.total_tokens || 0,
            analyticsData.previous_period.total_tokens || 0
        );

        const totalEstimatedCost = (analyticsData.tokens_by_organization_model || []).reduce(
            (sum, item) => sum + calculateCostUsd(item.model || "unknown", item.input_tokens || 0, item.output_tokens || 0),
            0
        );
        const llmConversationCount = analyticsData.llm_conversation_count || 0;
        const avgCostPerConversation = llmConversationCount > 0
            ? totalEstimatedCost / llmConversationCount
            : 0;

        const tokenOrCostCard = tokenChartView === "tokens"
            ? {
                title: "Total Tokens",
                value: (analyticsData.total_tokens || 0).toLocaleString(),
                change: tokenChange,
                icon: Database,
                description: "Input + output tokens"
            }
            : {
                title: "Total Cost",
                value: `$${totalEstimatedCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                change: null,
                icon: Database,
                description: "Estimated USD from model pricing"
            };

        return [
            {
                title: "Total Conversations",
                value: analyticsData.total_conversations.toLocaleString(),
                change: conversationChange,
                icon: MessagesSquare,
                description: "Across all organizations"
            },
            {
                title: "Total Messages", 
                value: analyticsData.total_messages.toLocaleString(),
                change: messageChange,
                icon: MessageCircle,
                description: "Across all organizations"
            },
            {
                title: "Total Contacts",
                value: analyticsData.total_leads.toLocaleString(),
                change: leadChange,
                icon: Users,
                description: "Across all organizations"
            },
            {
                title: "Avg Cost / Conversation",
                value: `$${avgCostPerConversation.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`,
                change: null,
                icon: MessageCircle,
                description: "Estimated USD per LLM conversation"
            },
            tokenOrCostCard
        ];
    };

    // Combine data for the main chart
    const getCombinedChartData = () => {
        if (!analyticsData) return [];

        const dateMap = new Map();

        // Add conversations data
        analyticsData.conversations_per_day.forEach(item => {
            dateMap.set(item.day, { 
                day: item.day, 
                conversations: item.conversation_count,
                messages: 0,
                leads: 0
            });
        });

        // Add messages data
        analyticsData.messages_per_day.forEach(item => {
            const existing = dateMap.get(item.day) || { day: item.day, conversations: 0, messages: 0, leads: 0 };
            existing.messages = item.message_count;
            dateMap.set(item.day, existing);
        });

        // Add leads data
        analyticsData.leads_per_day.forEach(item => {
            const existing = dateMap.get(item.day) || { day: item.day, conversations: 0, messages: 0, leads: 0 };
            existing.leads = item.lead_count;
            dateMap.set(item.day, existing);
        });

        return Array.from(dateMap.values()).sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());
    };

    const getTokensByOrganizationModelData = () => {
        if (!analyticsData) {
            return { data: [] as Array<Record<string, string | number>>, models: [] as string[] };
        }

        const modelSet = new Set<string>();
        const orgMap = new Map<string, Record<string, string | number>>();

        for (const item of analyticsData.tokens_by_organization_model || []) {
            const organizationName = item.organization_name || "Unknown Organization";
            const modelName = item.model || "unknown";
            const tokenCount = item.total_tokens || 0;
            const inputTokens = item.input_tokens || 0;
            const outputTokens = item.output_tokens || 0;
            const valueForView = tokenChartView === "tokens"
                ? tokenCount
                : calculateCostUsd(modelName, inputTokens, outputTokens);

            modelSet.add(modelName);

            if (!orgMap.has(organizationName)) {
                orgMap.set(organizationName, { organization_name: organizationName });
            }

            const row = orgMap.get(organizationName)!;
            const existingValue = typeof row[modelName] === "number" ? (row[modelName] as number) : 0;
            row[modelName] = existingValue + valueForView;
        }

        const models = Array.from(modelSet);
        const data = Array.from(orgMap.values())
            .map((row) => {
                const total = models.reduce((sum, modelName) => {
                    const value = typeof row[modelName] === "number" ? (row[modelName] as number) : 0;
                    return sum + value;
                }, 0);
                return { ...row, __total_tokens: total };
            })
            .sort((a, b) => (b.__total_tokens as number) - (a.__total_tokens as number))
            .slice(0, 8);

        return { data, models };
    };

    const getUsageTrendData = () => {
        const periodMap = new Map<string, { period: string; api: number; mcp: number; failures: number }>();
        for (const item of usageSummary?.trend || []) {
            const row = periodMap.get(item.period) || { period: item.period, api: 0, mcp: 0, failures: 0 };
            if (item.surface === "api") {
                row.api += item.request_count || 0;
            } else if (item.surface === "mcp") {
                row.mcp += item.request_count || 0;
            }
            row.failures += item.failure_count || 0;
            periodMap.set(item.period, row);
        }
        return Array.from(periodMap.values()).sort(
            (a, b) => new Date(a.period).getTime() - new Date(b.period).getTime()
        );
    };

    if (isLoading) {
        return (
            <TabsContent value="analytics" className="space-y-4">
                <LoadingState label="Loading analytics..." className="min-h-96" />
            </TabsContent>
        );
    }

    const metricCards = getMetricCards();
    const combinedData = getCombinedChartData();
    const tokenByOrgModel = getTokensByOrganizationModelData();
    const usageTrendData = getUsageTrendData();

    return (
        <TabsContent value="analytics" className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold">Admin Analytics</h3>
                    <p className="text-sm text-muted-foreground">
                        Aggregate data across all organizations
                    </p>
                </div>
                <Select value={timeRange} onValueChange={(val) => setTimeRange(val)}>
                    <SelectTrigger className="w-[180px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {timeRangeOptions.map(option => (
                            <SelectItem key={option.value} value={option.value}>
                                {option.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Metric Cards */}
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                {metricCards.map((card, index) => {
                    const Icon = card.icon;
                    const hasChange = typeof card.change === "number" && Number.isFinite(card.change);
                    const isPositive = hasChange && card.change >= 0;
                    
                    return (
                        <Card key={index}>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">
                                    {card.title}
                                </CardTitle>
                                <Icon className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{card.value}</div>
                                {hasChange ? (
                                    <p className="text-xs text-muted-foreground flex items-center">
                                        {isPositive ? (
                                            <ArrowUpIcon className="mr-1 h-3 w-3 text-green-600" />
                                        ) : (
                                            <ArrowDownIcon className="mr-1 h-3 w-3 text-red-600" />
                                        )}
                                        <span className={isPositive ? "text-green-600" : "text-red-600"}>
                                            {Math.abs(card.change).toFixed(1)}%
                                        </span>
                                        <span className="ml-1">{card.description}</span>
                                    </p>
                                ) : (
                                    <p className="text-xs text-muted-foreground">{card.description}</p>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            <Card>
                <CardHeader>
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <CardTitle>API/MCP Usage</CardTitle>
                            <CardDescription>Request volume, failures, and recent usage logs.</CardDescription>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Select value={usageOrganizationId} onValueChange={setUsageOrganizationId}>
                                <SelectTrigger className="w-[220px]">
                                    <SelectValue placeholder="Organization" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Organizations</SelectItem>
                                    {organizations.map((org) => (
                                        <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select value={usageSurface} onValueChange={setUsageSurface}>
                                <SelectTrigger className="w-[130px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All</SelectItem>
                                    <SelectItem value="api">API</SelectItem>
                                    <SelectItem value="mcp">MCP</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={usageStatus} onValueChange={setUsageStatus}>
                                <SelectTrigger className="w-[140px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Statuses</SelectItem>
                                    <SelectItem value="success">Success</SelectItem>
                                    <SelectItem value="error">Error</SelectItem>
                                </SelectContent>
                            </Select>
                            <Input
                                className="w-[220px]"
                                placeholder="Filter operation..."
                                value={usageOperation}
                                onChange={(event) => setUsageOperation(event.target.value)}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="grid gap-4 md:grid-cols-5">
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium">Requests</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{(usageSummary?.summary.request_count || 0).toLocaleString()}</div>
                                <p className="text-xs text-muted-foreground">API + MCP calls</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium">Failures</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{(usageSummary?.summary.failure_count || 0).toLocaleString()}</div>
                                <p className="text-xs text-muted-foreground">{formatPercent(usageSummary?.summary.failure_rate || 0)} failure rate</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium">Unique Credentials</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{(usageSummary?.summary.unique_credentials || 0).toLocaleString()}</div>
                                <p className="text-xs text-muted-foreground">API keys/OAuth families</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium">Avg Duration</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{Math.round(usageSummary?.summary.avg_duration_ms || 0)}ms</div>
                                <p className="text-xs text-muted-foreground">Best-effort request timing</p>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-medium">Max Duration</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{(usageSummary?.summary.max_duration_ms || 0).toLocaleString()}ms</div>
                                <p className="text-xs text-muted-foreground">Slowest recorded call</p>
                            </CardContent>
                        </Card>
                    </div>

                    {usageLoading ? (
                        <LoadingState label="Loading usage..." />
                    ) : (
                        <div className="grid gap-6 xl:grid-cols-2">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Usage Trend</CardTitle>
                                    <CardDescription>Requests over time by surface.</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <ResponsiveContainer width="100%" height={280}>
                                        <ComposedChart data={usageTrendData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="period" tickFormatter={formatUsagePeriod} />
                                            <YAxis />
                                            <Tooltip labelFormatter={(value: string | number) => formatUsagePeriod(String(value))} />
                                            <Legend />
                                            <Area type="monotone" dataKey="api" name="API" stackId="1" stroke="#2563eb" fill="#2563eb" fillOpacity={0.55} />
                                            <Area type="monotone" dataKey="mcp" name="MCP" stackId="1" stroke="#16a34a" fill="#16a34a" fillOpacity={0.55} />
                                            <Line type="monotone" dataKey="failures" name="Failures" stroke="#dc2626" strokeWidth={2} dot={false} />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Top Operations</CardTitle>
                                    <CardDescription>Highest-volume API routes and MCP tools.</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="border rounded-lg overflow-hidden">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Operation</TableHead>
                                                    <TableHead>Surface</TableHead>
                                                    <TableHead className="text-right">Requests</TableHead>
                                                    <TableHead className="text-right">Failures</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {(usageSummary?.top_operations || []).map((operation) => (
                                                    <TableRow key={`${operation.surface}:${operation.operation}`}>
                                                        <TableCell className="font-mono text-xs">{operation.operation}</TableCell>
                                                        <TableCell className="uppercase">{operation.surface}</TableCell>
                                                        <TableCell className="text-right">{operation.request_count.toLocaleString()}</TableCell>
                                                        <TableCell className="text-right">{operation.failure_count.toLocaleString()}</TableCell>
                                                    </TableRow>
                                                ))}
                                                {(usageSummary?.top_operations || []).length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                                                            No API/MCP usage found for these filters.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    <div className="border rounded-lg overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Organization</TableHead>
                                    <TableHead>Surface</TableHead>
                                    <TableHead>Operation</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="text-right">Duration</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {usageEventsLoading && (!usageEvents || usageEvents.items.length === 0) ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">Loading usage logs...</TableCell>
                                    </TableRow>
                                ) : (
                                    (usageEvents?.items || []).map((event) => (
                                        <TableRow key={event.id}>
                                            <TableCell className="text-xs">{event.created_at ? new Date(event.created_at).toLocaleString() : '-'}</TableCell>
                                            <TableCell>{event.organization_name || event.organization_id}</TableCell>
                                            <TableCell className="uppercase">{event.surface}</TableCell>
                                            <TableCell className="font-mono text-xs">{event.operation}</TableCell>
                                            <TableCell>
                                                <Badge variant={event.status === "success" ? "default" : "destructive"}>
                                                    {event.status}{event.status_code ? ` ${event.status_code}` : ''}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-right">{event.duration_ms ?? '-'}ms</TableCell>
                                        </TableRow>
                                    ))
                                )}
                                {!usageEventsLoading && (usageEvents?.items || []).length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">No usage events found.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                        <div className="text-muted-foreground">
                            {usageEvents ? `${usageEvents.total.toLocaleString()} total usage events` : 'No usage events loaded'}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setUsageEventsPage((page) => Math.max(1, page - 1))}
                                disabled={!usageEvents || usageEvents.page <= 1}
                            >
                                Previous
                            </Button>
                            <span className="text-muted-foreground">
                                Page {usageEvents?.page || 1} of {usageEvents?.total_pages || 1}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setUsageEventsPage((page) => page + 1)}
                                disabled={!usageEvents || usageEvents.page >= usageEvents.total_pages}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Primary Cost/Tokens Chart - Full Width */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between gap-2">
                        <CardTitle>
                            {tokenChartView === "tokens" ? "Tokens by Organization" : "Cost by Organization"}
                        </CardTitle>
                        <Select value={tokenChartView} onValueChange={(val) => setTokenChartView(val as "tokens" | "cost")}>
                            <SelectTrigger className="w-[150px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="tokens">Token Count</SelectItem>
                                <SelectItem value="cost">Cost (USD)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <CardDescription>
                        Grouped by model name ({tokenChartView === "tokens" ? "tokens" : "estimated USD"})
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <ChartContainer config={chartConfig}>
                        <ResponsiveContainer width="100%" height={360}>
                            <BarChart data={tokenByOrgModel.data}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="organization_name"
                                    angle={-30}
                                    textAnchor="end"
                                    height={72}
                                    axisLine={false}
                                    tickLine={false}
                                    fontSize={11}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    fontSize={12}
                                    tickFormatter={(value) => tokenChartView === "cost"
                                        ? `$${Number(value).toFixed(2)}`
                                        : Number(value).toLocaleString()}
                                />
                                <ChartTooltip
                                    content={<ChartTooltipContent />}
                                    formatter={(value: number) => tokenChartView === "cost"
                                        ? `$${Number(value).toFixed(4)}`
                                        : Number(value).toLocaleString()}
                                />
                                <Legend />
                                {tokenByOrgModel.models.map((modelName, index) => (
                                    <Bar
                                        key={modelName}
                                        dataKey={modelName}
                                        name={modelName}
                                        fill={modelSeriesColors[index % modelSeriesColors.length]}
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartContainer>
                </CardContent>
            </Card>

            {/* Secondary Charts Grid */}
            <div className="grid gap-4 md:grid-cols-2">
                {/* Activity Over Time */}
                <Card>
                    <CardHeader>
                        <CardTitle>Activity Over Time</CardTitle>
                        <CardDescription>
                            Conversations, messages, and contacts trends
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ChartContainer config={chartConfig}>
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={combinedData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="day" 
                                        tickFormatter={formatDate}
                                        axisLine={false}
                                        tickLine={false}
                                        fontSize={12}
                                    />
                                    <YAxis axisLine={false} tickLine={false} fontSize={12} />
                                    <ChartTooltip content={<ChartTooltipContent />} />
                                    <Area
                                        type="monotone"
                                        dataKey="conversations"
                                        stackId="1"
                                        stroke="var(--color-conversations)"
                                        fill="var(--color-conversations)"
                                        fillOpacity={0.8}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="messages"
                                        stackId="1"
                                        stroke="var(--color-messages)"
                                        fill="var(--color-messages)"
                                        fillOpacity={0.8}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="leads"
                                        stackId="1"
                                        stroke="var(--color-leads)"
                                        fill="var(--color-leads)"
                                        fillOpacity={0.8}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </ChartContainer>
                    </CardContent>
                </Card>

                {/* New Visitors Per Day */}
                <Card>
                    <CardHeader>
                        <CardTitle>New Visitors</CardTitle>
                        <CardDescription>
                            New visitor profiles per {timeRange === '24h' ? 'hour' : timeRange === '90d' ? 'week' : timeRange === '365d' ? 'month' : 'day'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ChartContainer config={chartConfig}>
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart
                                    data={(analyticsData?.visitors_per_day || [])
                                        .slice()
                                        .sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime())}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="day"
                                        tickFormatter={formatDate}
                                        axisLine={false}
                                        tickLine={false}
                                        fontSize={12}
                                    />
                                    <YAxis axisLine={false} tickLine={false} fontSize={12} />
                                    <ChartTooltip content={<ChartTooltipContent />} />
                                    <Area
                                        type="monotone"
                                        dataKey="visitor_count"
                                        name="New Visitors"
                                        stroke="var(--color-visitors)"
                                        fill="var(--color-visitors)"
                                        fillOpacity={0.6}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </ChartContainer>
                    </CardContent>
                </Card>

                {/* Conversations by Organization */}
                <Card>
                    <CardHeader>
                        <CardTitle>Conversations by Organization</CardTitle>
                        <CardDescription>Top organizations by conversations</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ChartContainer config={chartConfig}>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={analyticsData?.conversations_by_organization.slice(0, 8)}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="organization_name" 
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        axisLine={false}
                                        tickLine={false}
                                        fontSize={10}
                                    />
                                    <YAxis axisLine={false} tickLine={false} fontSize={12} />
                                    <ChartTooltip content={<ChartTooltipContent />} />
                                    <Bar 
                                        dataKey="conversation_count" 
                                        fill="var(--color-conversations)"
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartContainer>
                    </CardContent>
                </Card>

                {/* Messages by Organization */}
                <Card>
                    <CardHeader>
                        <CardTitle>Messages by Organization</CardTitle>
                        <CardDescription>Top organizations by messages</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ChartContainer config={chartConfig}>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={analyticsData?.messages_by_organization.slice(0, 8)}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="organization_name" 
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        axisLine={false}
                                        tickLine={false}
                                        fontSize={10}
                                    />
                                    <YAxis axisLine={false} tickLine={false} fontSize={12} />
                                    <ChartTooltip content={<ChartTooltipContent />} />
                                    <Bar 
                                        dataKey="message_count" 
                                        fill="var(--color-messages)"
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartContainer>
                    </CardContent>
                </Card>

                {/* Leads by Organization */}
                <Card>
                    <CardHeader>
                        <CardTitle>Contacts by Organization</CardTitle>
                        <CardDescription>Top organizations by contacts</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <ChartContainer config={chartConfig}>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={analyticsData?.leads_by_organization.slice(0, 8)}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="organization_name" 
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        axisLine={false}
                                        tickLine={false}
                                        fontSize={10}
                                    />
                                    <YAxis axisLine={false} tickLine={false} fontSize={12} />
                                    <ChartTooltip content={<ChartTooltipContent />} />
                                    <Bar 
                                        dataKey="lead_count" 
                                        fill="var(--color-leads)"
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartContainer>
                    </CardContent>
                </Card>

            </div>

        </TabsContent>
    );
};

export default AdminAnalyticsTab;